/**
 * RevenueCat Web Billing SDK wrapper.
 *
 * Isolates every SDK call so the rest of the app never imports
 * `@revenuecat/purchases-js` directly. The mobile app has a matching module
 * with the same exported shape backed by `react-native-purchases`.
 *
 * Only the PUBLIC web billing key is used here. The secret key is server-side.
 */

import { Purchases } from '@revenuecat/purchases-js';
import { revenueCatConfig, isBillingConfigured } from '../../config/appConfig.js';

let configuredAppUserId = null;

/** True when a public billing key is present. */
export function isConfigured() {
  return isBillingConfigured;
}

/**
 * Configures the SDK with the Firebase UID as the App User ID.
 *
 * Using the Firebase UID (never the email address) is what keeps one identity
 * across web, iOS and Android for the same person.
 */
export async function configure(firebaseUid) {
  if (!isBillingConfigured) return null;
  if (!firebaseUid) throw new Error('A Firebase UID is required before configuring billing.');

  if (Purchases.isConfigured() && configuredAppUserId === firebaseUid) {
    return Purchases.getSharedInstance();
  }

  if (Purchases.isConfigured() && configuredAppUserId !== firebaseUid) {
    // Switching accounts in the same tab.
    await Purchases.getSharedInstance().changeUser(firebaseUid);
    configuredAppUserId = firebaseUid;
    return Purchases.getSharedInstance();
  }

  Purchases.configure({ apiKey: revenueCatConfig.apiKey, appUserId: firebaseUid });
  configuredAppUserId = firebaseUid;

  return Purchases.getSharedInstance();
}

/** Clears billing state on sign-out so the next user starts clean. */
export async function reset() {
  configuredAppUserId = null;
  if (!isBillingConfigured || !Purchases.isConfigured()) return;

  try {
    await Purchases.getSharedInstance().close();
  } catch {
    // Closing an already-closed instance is not an error worth surfacing.
  }
}

/** Fetches the current offerings. Returns null when billing is unconfigured. */
export async function getOfferings() {
  if (!isBillingConfigured || !Purchases.isConfigured()) return null;
  return Purchases.getSharedInstance().getOfferings();
}

/** Fetches the current customer info. */
export async function getCustomerInfo() {
  if (!isBillingConfigured || !Purchases.isConfigured()) return null;
  return Purchases.getSharedInstance().getCustomerInfo();
}

/**
 * Starts checkout for a package.
 * The SDK opens RevenueCat's hosted purchase flow.
 */
export async function purchasePackage(rcPackage, { email } = {}) {
  if (!isBillingConfigured || !Purchases.isConfigured()) {
    throw new Error('Billing is not configured on this deployment.');
  }

  return Purchases.getSharedInstance().purchase({
    rcPackage,
    ...(email ? { customerEmail: email } : {}),
  });
}

/** True when the customer holds the `pro` entitlement right now. */
export function hasProEntitlement(customerInfo) {
  const entitlement = customerInfo?.entitlements?.active?.[revenueCatConfig.entitlementId];
  return Boolean(entitlement);
}

/** The active `pro` entitlement object, or null. */
export function getProEntitlement(customerInfo) {
  return customerInfo?.entitlements?.active?.[revenueCatConfig.entitlementId] ?? null;
}

export { Purchases };
